import { useCallback, useMemo, useEffect, useState } from 'react';
import _ from 'lodash';
import { SaveEditRequest } from 'react-scale/components/audits/SpeedAudit/types';
import {
  queryClient,
  saveResponseEdit,
  undoResponseEdits,
} from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditApi';
import { toast } from 'react-toastify';
import { useUser } from 'react-scale/components/ui/UserContext';

interface UseEditManagementProps {
  auditAttemptId?: string;
  originalResponses: any;
  readOnly: boolean;
}

export const useEditManagement = ({
  auditAttemptId,
  originalResponses,
  readOnly,
}: UseEditManagementProps) => {
  const user = useUser();
  const [showConfirmRevertModal, setShowConfirmRevertModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [revertCount, setRevertCount] = useState<number>(0);

  const submitEdit = useCallback(
    async (responses: any) => {
      try {
        if (!auditAttemptId || _.isEmpty(responses)) {
          return;
        }

        const saveEditRequest: SaveEditRequest = {
          reviewer: user.id,
          reviewChanges: responses,
          attemptAuditId: auditAttemptId,
        };

        await saveResponseEdit(saveEditRequest);

        toast(`Saved edits`, {
          type: 'success',
          autoClose: 2000,
          position: 'top-right',
        });

        await queryClient.invalidateQueries(['attemptAuditResponse']);
      } catch (err: any) {
        toast(`Failed to save edits: ${err.message}`, {
          type: 'error',
          autoClose: 2000,
          position: 'top-right',
        });
      }
    },
    [user.id, auditAttemptId],
  );

  const debouncedSubmitEdit = useMemo(
    () => _.debounce((responses: any) => submitEdit(responses), 200),
    [submitEdit],
  );

  const submitRevert = useCallback(async () => {
    try {
      if (!auditAttemptId) {
        return;
      }

      await undoResponseEdits(auditAttemptId);

      toast(`Reverted edits`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });

      await queryClient.invalidateQueries(['attemptAuditResponse']);

      // Increment revert count to trigger re-render
      setTimeout(() => setRevertCount(prev => prev + 1), 1000);
    } catch (err: any) {
      toast(`Failed to revert edits: ${err.message}`, {
        type: 'error',
        autoClose: 2000,
        position: 'top-right',
      });
    }
  }, [auditAttemptId]);

  const handleSubmitRevert = useCallback(() => {
    setPendingAction(() => submitRevert);
    setShowConfirmRevertModal(true);
  }, [submitRevert]);

  const handleConfirmRevert = useCallback(() => {
    setShowConfirmRevertModal(false);
    if (pendingAction) {
      pendingAction();
    }
  }, [pendingAction]);

  const handleCancelRevert = useCallback(() => {
    setShowConfirmRevertModal(false);
    setPendingAction(null);
  }, []);

  // Auto-save edits when responses change (for non-readonly mode)
  const handleResponsesChange = useCallback(
    (currentResponses: any) => {
      if (!readOnly && !_.isEqual(currentResponses, originalResponses)) {
        debouncedSubmitEdit(currentResponses);
      }
    },
    [readOnly, originalResponses, debouncedSubmitEdit],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSubmitEdit.cancel();
    };
  }, [debouncedSubmitEdit]);

  return {
    submitEdit,
    submitRevert,
    handleSubmitRevert,
    handleConfirmRevert,
    handleCancelRevert,
    handleResponsesChange,
    showConfirmRevertModal,
    revertCount,
    debouncedSubmitEdit,
  };
};
